import { connect } from 'react-redux';
import { FieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { useCallback } from 'react';

import { KNM_LAWSUITS } from 'CorePackage/MainModule/routes/routes';
import {
  VIOLATIONS_LAWSUITS_TITLE_BLOCK_ID,
  getViolationLawsuitBlockId,
} from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmErpResultViolationSelectOption } from 'KnmErpPackage/MainModule/interfaces/IKnmErpResultViolationSelectOption';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import knmSharedStyles from 'KnmPackage/MainModule/styles/KnmShared.module.scss';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Section } from 'UIPackage/FormModule/components/Section';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { IKnmErpFormViolationsLawsuit } from '../interfaces/IKnmErpFormViolationsLawsuit';
import { KnmErpLawsuit } from './KnmErpLawsuit';

interface IKnmErpLawsuitsProps {
  error?: string;
  knmResultsViolationsSelectOptions: IKnmErpResultViolationSelectOption[] | undefined;
  violationsLawsuits: IKnmForm['violationsLawsuits'];
  watchOnly?: boolean;
}

interface IKnmErpLawsuitsStoreProps {
  formRef: IKnmFormRef | undefined;
}

const KnmErpLawsuitsWithoutStore = ({
  error,
  knmResultsViolationsSelectOptions,
  violationsLawsuits,
  watchOnly,
  formRef,
}: IKnmErpLawsuitsProps & IKnmErpLawsuitsStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const addViolationLawsuit = useCallback(
    () => !watchOnly && push && push('violationsLawsuits', { isNew: true, listId: uuidv4(), lawsuits: [{}] }),
    [push, watchOnly],
  );
  const makeRemoveViolationLawsuitHandler = (index: number) => () =>
    !watchOnly && remove && remove('violationsLawsuits', index);

  return (
    <Section
      title="Меры, принятые по результатам КНМ"
      id={KNM_LAWSUITS}
      titleId={VIOLATIONS_LAWSUITS_TITLE_BLOCK_ID}
      customButton={
        <Button
          className="shared-btn-add"
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          disabled={watchOnly || Boolean(error)}
          onClick={addViolationLawsuit}
        />
      }
      type="list"
    >
      {error && (
        <Row>
          <p className={knmSharedStyles.SectionErrorNotification}>{error}</p>
        </Row>
      )}

      <FieldArray name="violationsLawsuits">
        {({ fields }) => {
          if (!fields.length) {
            return null;
          }

          return fields.map((name, index) => {
            const fieldValue: IKnmErpFormViolationsLawsuit = fields.value?.[index];
            const id = getViolationLawsuitBlockId(fieldValue.listId);

            return (
              <KnmErpLawsuit
                key={id}
                formRef={formRef}
                id={id}
                index={index}
                knmResultsViolationsSelectOptions={knmResultsViolationsSelectOptions}
                name={name}
                value={fieldValue}
                violationsLawsuits={violationsLawsuits}
                watchOnly={watchOnly}
                onRemove={makeRemoveViolationLawsuitHandler(index)}
              />
            );
          });
        }}
      </FieldArray>
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IKnmErpLawsuitsStoreProps => ({
  formRef: getKnmFormRef(state),
});

export const KnmErpLawsuits = connect(mapStateToProps)(KnmErpLawsuitsWithoutStore);
